import {
    getMailboxInfoByIndexerValue,
    isSameMailboxInfo,
    getGlobalSettingsAccountMailboxInfo,
} from 'owa-account-source-list-store';
import type { MailboxInfo } from 'owa-client-types';
import type { ResolverContext } from 'owa-graph-schema';
import { trace, errorThatWillCauseAlert } from 'owa-trace';
import { type TraceErrorObject } from 'owa-analytics-types';
import { logUsage } from 'owa-analytics';
import type { Operation } from '../types/Operation';
import { fromNodeID } from 'global-object-identification';

// 'Strict' version of getMailboxInfoFromOperation that returns undefined if a mailboxInfo is not
// found on the operation (getMailboxInfoFromOperation falls back to the global account mailboxInfo)
export function getMailboxInfoFromOperationStrict(operation: Operation): MailboxInfo | undefined {
    const mailboxInfo: MailboxInfo | undefined =
        (operation.getContext() as ResolverContext)?.requestOptions?.mailboxInfo ||
        getMailboxInfoFromVariables(operation);

    return mailboxInfo;
}

type kv = { key: string; val: any; path: string };

// do a simple BFS through the variables looking for a field named mailboxinfo.
// note: it's not enough just to look at the toplevel fields in 'variables' because,
// for example, it's very common for graphql inputs to have a single input object that roots
// the actual runtime variables.
function getMailboxInfoFromVariables(operation: Operation): MailboxInfo | undefined {
    const vars = operation.variables || {};
    const worklist: kv[] = [{ key: 'variables', val: vars, path: 'variables' }];
    const isPeopleAppSearchOp =
        operation.operationName === 'peopleAppSearchView_Query' ||
        operation.operationName === 'SearchResultsPaginationQuery';
    let errorMsg = undefined;
    let sanity = 1024;
    let rv: MailboxInfo | undefined;
    let mailboxInfoPath: string | undefined;

    while (worklist.length > 0 && --sanity > 0) {
        const work = worklist.shift();
        if (work) {
            if (
                work.key === 'mailboxinfo' ||
                (isPeopleAppSearchOp && work.key === 'filtersearchby')
            ) {
                const mailboxInfo: MailboxInfo | undefined =
                    work.key === 'mailboxinfo'
                        ? work.val
                        : getMailboxInfoFromNodeId(work.val.accountNodeId);
                if (rv === undefined) {
                    rv = mailboxInfo;
                    mailboxInfoPath = work.path;
                } else {
                    // notify if there's a single operation trying to access two different remote mailboxes
                    // the gateway currently assumes a single routing header and auth context per request so
                    // to support this, we'd need to break the request into separate requests based on MI and
                    // route separately and reassemble the results.
                    if (mailboxInfo && !isSameMailboxInfo(rv, mailboxInfo)) {
                        errorMsg = `Operation ${
                            operation.operationName
                        } is attempting a remote query for two different mailbox identities at ${mailboxInfoPath}: ${JSON.stringify(
                            rv
                        )} and at ${work.path}: ${JSON.stringify(
                            mailboxInfo
                        )}.  This is not currently supported.`;
                    }
                }
            } else if (work.val) {
                for (const key of Object.keys(work.val)) {
                    const val = work.val[key];
                    if (typeof val === 'object') {
                        const path = Array.isArray(work.val)
                            ? `${work.path}[${key}]`
                            : `${work.path}.${key}`;
                        worklist.push({ key: key.toString().toLowerCase(), val, path });
                    }
                }
            }
        }
    }
    if (errorMsg) {
        trace.warn(errorMsg);
        const error: TraceErrorObject = new Error('getMailboxInfoFromVariables');
        error.additionalInfo = { errorMsg };
        errorThatWillCauseAlert(error);
    }

    if (!rv && sanity <= 0) {
        // if this is hit, we searched far more fields than we'd expect to be in an input variable.
        // we'll use a MI if it was found, otherwise just default to the global acct
        // if a secondary account feature needs this working, they can set it on the context or move the mailboxinfo earlier
        // in the tree.
        trace.warn("couldn't infer mailboxinfo from variabes.  Object too complex");
        // We cannot determine which account this is for, so we use the global settings account for this infra-level telemetry.
        logUsage('Get_MI_Op_Complex', getGlobalSettingsAccountMailboxInfo(), {
            name: operation.operationName,
        });
    }

    return rv;
}

const getMailboxInfoFromNodeId = (accountNodeId: string): MailboxInfo | undefined => {
    const [, accountId] = fromNodeID(accountNodeId);
    return getMailboxInfoByIndexerValue(accountId);
};
